import type {
  CompanyType,
  ProductType,
  MarketType,
  ProjectStatus,
  TargetRegion,
} from '@/lib/api/client'
import {
  COMPANY_TYPE_LABELS,
  COMPANY_TYPE_OPTIONS,
  MARKET_LABELS,
  MARKET_OPTIONS,
  PRODUCT_TYPE_LABELS,
  PRODUCT_TYPE_OPTIONS,
  PROJECT_STATUS_LABELS,
  PROJECT_STATUS_OPTIONS,
  TARGET_REGION_LABELS,
  TARGET_REGION_OPTIONS,
} from './portfolioConstants'

export interface MetadataValues {
  companyType: CompanyType | null
  productType: ProductType | null
  market: MarketType | null
  industry: string
  targetRegion: TargetRegion | null
  clientName: string
  status: ProjectStatus
  tags: string[]
}

export const EMPTY_METADATA: MetadataValues = {
  companyType: null,
  productType: null,
  market: null,
  industry: '',
  targetRegion: null,
  clientName: '',
  status: 'active',
  tags: [],
}

interface ProjectMetadataFieldsProps {
  values: MetadataValues
  onChange: (next: MetadataValues) => void
  showStatus?: boolean
}

const selectClass =
  'w-full h-9 px-3 bg-warm-50 border border-border rounded-lg text-sm text-text focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/15 appearance-none'
const inputClass =
  'w-full h-9 px-3 bg-warm-50 border border-border rounded-lg text-sm text-text placeholder-text-muted focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/15'
const labelClass =
  'block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wide'

export function ProjectMetadataFields({ values, onChange, showStatus = false }: ProjectMetadataFieldsProps) {
  const set = <K extends keyof MetadataValues>(key: K, value: MetadataValues[K]) =>
    onChange({ ...values, [key]: value })

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Company stage</label>
          <select
            value={values.companyType ?? ''}
            onChange={(e) => set('companyType', (e.target.value || null) as CompanyType | null)}
            className={selectClass}
          >
            <option value="">—</option>
            {COMPANY_TYPE_OPTIONS.map((v) => (
              <option key={v} value={v}>
                {COMPANY_TYPE_LABELS[v]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Market</label>
          <select
            value={values.market ?? ''}
            onChange={(e) => set('market', (e.target.value || null) as MarketType | null)}
            className={selectClass}
          >
            <option value="">—</option>
            {MARKET_OPTIONS.map((v) => (
              <option key={v} value={v}>
                {MARKET_LABELS[v]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Product type</label>
          <select
            value={values.productType ?? ''}
            onChange={(e) => set('productType', (e.target.value || null) as ProductType | null)}
            className={selectClass}
          >
            <option value="">—</option>
            {PRODUCT_TYPE_OPTIONS.map((v) => (
              <option key={v} value={v}>
                {PRODUCT_TYPE_LABELS[v]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Industry</label>
          <input
            type="text"
            value={values.industry}
            onChange={(e) => set('industry', e.target.value)}
            placeholder="Fintech, Climate, Manufacturing…"
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Target region</label>
          <select
            value={values.targetRegion ?? ''}
            onChange={(e) => set('targetRegion', (e.target.value || null) as TargetRegion | null)}
            className={selectClass}
          >
            <option value="">—</option>
            {TARGET_REGION_OPTIONS.map((v) => (
              <option key={v} value={v}>
                {TARGET_REGION_LABELS[v]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>
            Client <span className="text-text-muted normal-case font-normal">— optional</span>
          </label>
          <input
            type="text"
            value={values.clientName}
            onChange={(e) => set('clientName', e.target.value)}
            placeholder="Internal, or client name"
            className={inputClass}
          />
        </div>
        {showStatus && (
          <div>
            <label className={labelClass}>Status</label>
            <select
              value={values.status}
              onChange={(e) => set('status', e.target.value as ProjectStatus)}
              className={selectClass}
            >
              {PROJECT_STATUS_OPTIONS.map((v) => (
                <option key={v} value={v}>
                  {PROJECT_STATUS_LABELS[v]}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  )
}
